import { Link } from 'react-router';
import { alert } from '@/services/alert';
import { TimeInput } from '@mantine/dates';
import { IconAlertTriangle, IconCalendarEvent } from '@tabler/icons-react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { getBrowserTimezone } from '@/pages/SchedulingPage/utils/common';
import { TimezoneInput } from '@/pages/SchedulingPage/components/TimezoneInput';
import { Alert, Button, Divider, Drawer, Flex, Group, Loader, Select, Stack, Switch, Text } from '@mantine/core';
import { convertTimeFromHHMMSS, convertTimeToHHMMSS } from '@/pages/SchedulingPage/utils/availability';
import { DEFAULT_ON_CALL_START_TIME, IvrHoursSetId } from '@bariendo/lib/constants';
import { DEFAULT_IVR_MENU_SETTINGS, type IvrHoursSet, type IvrMenuSettings } from '@bariendo/lib/ivr';

interface DayBlockFormValues {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface HoursSetFormValues {
  weekday: DayBlockFormValues;
  weekend: DayBlockFormValues;
  timezone: string;
}

interface IVRHoursLanguageDrawerProps {
  opened: boolean;
  onClose: () => void;
  loading: boolean;
  hoursSets: Map<IvrHoursSetId, IvrHoursSet>;
  configuredHoursSetIds: Set<IvrHoursSetId>;
  menuSettings: IvrMenuSettings;
  usedDigits: Set<number>;
  onSaveHoursSet: (hoursSet: IvrHoursSet) => Promise<void>;
  onSaveMenuSettings: (settings: IvrMenuSettings) => Promise<void>;
}

const DEFAULT_WEEKDAY: DayBlockFormValues = { enabled: true, startTime: '09:00', endTime: '17:00' };
const DEFAULT_WEEKEND: DayBlockFormValues = { enabled: false, startTime: '10:00', endTime: '14:00' };

function toDayBlockForm(block: IvrHoursSet['weekday'], fallback: DayBlockFormValues): DayBlockFormValues {
  if (!block) {
    return { ...fallback, enabled: false };
  }

  return {
    enabled: true,
    startTime: convertTimeFromHHMMSS(block.startTime),
    endTime: convertTimeFromHHMMSS(block.endTime),
  };
}

function toHoursSetForm(hoursSet: IvrHoursSet, browserTimezone: string): HoursSetFormValues {
  return {
    weekday: toDayBlockForm(hoursSet.weekday, DEFAULT_WEEKDAY),
    weekend: toDayBlockForm(hoursSet.weekend, DEFAULT_WEEKEND),
    timezone: hoursSet.timezone ?? browserTimezone,
  };
}

function DayBlockFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DayBlockFormValues;
  onChange: (next: DayBlockFormValues) => void;
}): ReactElement {
  return (
    <Stack gap="xs">
      <Switch
        checked={value.enabled}
        label={label}
        onChange={(event) => onChange({ ...value, enabled: event.currentTarget.checked })}
      />
      {value.enabled && (
        <Flex gap="xs">
          <TimeInput
            label="Opens"
            style={{ flex: 1 }}
            value={value.startTime}
            onChange={(event) => onChange({ ...value, startTime: event.currentTarget?.value ?? '' })}
          />
          <TimeInput
            label="Closes"
            style={{ flex: 1 }}
            value={value.endTime}
            onChange={(event) => onChange({ ...value, endTime: event.currentTarget?.value ?? '' })}
          />
        </Flex>
      )}
    </Stack>
  );
}

/**
 * Warns when a set closes before on-call NP coverage is likely to begin.
 *
 * Deliberately phrased as "check", not "there is a gap": the real coverage lives in
 * separate on-call Schedule slots this drawer does not read, so this can only flag that the
 * two need comparing. A set that closes at 17:00 while on-call starts at 21:00 leaves
 * findActiveOnCallSlot returning null, and those callers are silently forwarded to the
 * virtual receptionist instead of reaching a nurse practitioner.
 */
function OnCallCoverageWarning({ values }: { values: HoursSetFormValues }): ReactElement | null {
  const closesBeforeOnCall = useMemo(() => {
    if (!values.weekday.enabled || !values.weekday.endTime) {
      return false;
    }

    return values.weekday.endTime < DEFAULT_ON_CALL_START_TIME;
  }, [values.weekday]);

  if (!closesBeforeOnCall) {
    return null;
  }

  return (
    <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
      <Stack gap="xs">
        <Text size="sm">
          This set closes at {values.weekday.endTime}. Confirm on-call NP coverage starts then, or callers routed to the
          on-call NP in the gap reach the virtual receptionist instead.
        </Text>
        <Button
          size="compact-xs"
          variant="subtle"
          component={Link}
          to="/on-call-schedule"
          leftSection={<IconCalendarEvent size={14} />}
        >
          Check on-call schedule
        </Button>
      </Stack>
    </Alert>
  );
}

export default function IVRHoursLanguageDrawer({
  opened,
  onClose,
  loading,
  hoursSets,
  configuredHoursSetIds,
  menuSettings,
  usedDigits,
  onSaveHoursSet,
  onSaveMenuSettings,
}: IVRHoursLanguageDrawerProps): ReactElement {
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const [forms, setForms] = useState<Map<IvrHoursSetId, HoursSetFormValues>>(new Map());
  const [settingsForm, setSettingsForm] = useState<IvrMenuSettings>(menuSettings);
  const [saving, setSaving] = useState(false);

  const orderedSets = useMemo(() => [...hoursSets.values()], [hoursSets]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    const next = new Map<IvrHoursSetId, HoursSetFormValues>();
    for (const hoursSet of hoursSets.values()) {
      next.set(hoursSet.hoursSetId, toHoursSetForm(hoursSet, browserTimezone));
    }
    setForms(next);
    setSettingsForm(menuSettings);
  }, [opened, hoursSets, menuSettings, browserTimezone]);

  const languageKeyOptions = useMemo(
    () =>
      Array.from({ length: 10 }, (_, digit) => ({
        value: String(digit),
        label: String(digit),
        disabled: usedDigits.has(digit) && digit !== settingsForm.languageSwitchKey,
      })),
    [usedDigits, settingsForm.languageSwitchKey]
  );

  const replayKeyOptions = useMemo(
    () => [
      { value: DEFAULT_IVR_MENU_SETTINGS.replayMenuKey, label: '* (asterisk)' },
      ...Array.from({ length: 10 }, (_, digit) => ({
        value: String(digit),
        label: String(digit),
        disabled: digit === settingsForm.languageSwitchKey,
      })),
    ],
    [settingsForm.languageSwitchKey]
  );

  const updateForm = (hoursSetId: IvrHoursSetId, next: HoursSetFormValues) => {
    setForms((current) => new Map(current).set(hoursSetId, next));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      for (const hoursSet of orderedSets) {
        const values = forms.get(hoursSet.hoursSetId);

        if (!values) {
          continue;
        }

        // Skip a set that is still entirely unconfigured, so opening and saving the drawer
        // does not create empty Schedules for sets nobody asked for.
        const isConfigured = configuredHoursSetIds.has(hoursSet.hoursSetId);
        if (!isConfigured && !values.weekday.enabled && !values.weekend.enabled) {
          continue;
        }

        if (!values.weekday.enabled && !values.weekend.enabled) {
          throw new Error(`${hoursSet.name} needs at least one open day, or every option on it is closed all week.`);
        }

        await onSaveHoursSet({
          hoursSetId: hoursSet.hoursSetId,
          name: hoursSet.name,
          weekday: values.weekday.enabled
            ? {
                startTime: convertTimeToHHMMSS(values.weekday.startTime),
                endTime: convertTimeToHHMMSS(values.weekday.endTime),
              }
            : undefined,
          weekend: values.weekend.enabled
            ? {
                startTime: convertTimeToHHMMSS(values.weekend.startTime),
                endTime: convertTimeToHHMMSS(values.weekend.endTime),
              }
            : undefined,
          timezone: values.timezone,
        });
      }

      await onSaveMenuSettings(settingsForm);
      alert.success('IVR hours saved.');
      onClose();
    } catch (error) {
      alert.error(error instanceof Error ? error.message : 'Failed to save IVR hours');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer.Root opened={opened} onClose={onClose} position="right" size="md">
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title fw="bold">Hours &amp; language</Drawer.Title>
          <Drawer.CloseButton />
        </Drawer.Header>

        <Drawer.Body>
          {loading ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : (
            <Stack gap="lg" py="sm">
              {orderedSets.map((hoursSet) => {
                const values = forms.get(hoursSet.hoursSetId);

                if (!values) {
                  return null;
                }

                return (
                  <Stack key={hoursSet.hoursSetId} gap="sm">
                    <Group gap="xs">
                      <Text fw={600}>{hoursSet.name}</Text>
                      {!configuredHoursSetIds.has(hoursSet.hoursSetId) && (
                        <Text size="xs" c="dimmed">
                          not configured yet
                        </Text>
                      )}
                    </Group>

                    <DayBlockFields
                      label="Weekdays (Mon–Fri)"
                      value={values.weekday}
                      onChange={(weekday) => updateForm(hoursSet.hoursSetId, { ...values, weekday })}
                    />

                    <DayBlockFields
                      label="Weekend (Sat–Sun)"
                      value={values.weekend}
                      onChange={(weekend) => updateForm(hoursSet.hoursSetId, { ...values, weekend })}
                    />

                    <TimezoneInput
                      value={values.timezone}
                      onChange={(timezone) => updateForm(hoursSet.hoursSetId, { ...values, timezone })}
                    />

                    <OnCallCoverageWarning values={values} />

                    <Divider />
                  </Stack>
                );
              })}

              <Text fw={600}>Menu keys</Text>

              <Select
                label="Language switch key"
                description="Reserved for language selection and unavailable for routing."
                data={languageKeyOptions}
                value={String(settingsForm.languageSwitchKey)}
                onChange={(value) =>
                  setSettingsForm((current) => ({
                    ...current,
                    languageSwitchKey: value ? Number(value) : DEFAULT_IVR_MENU_SETTINGS.languageSwitchKey,
                  }))
                }
                required
              />

              <Select
                label="Replay menu key"
                description="Caller presses this to replay the menu from the beginning."
                data={replayKeyOptions}
                value={settingsForm.replayMenuKey}
                onChange={(value) =>
                  setSettingsForm((current) => ({
                    ...current,
                    replayMenuKey: value ?? DEFAULT_IVR_MENU_SETTINGS.replayMenuKey,
                  }))
                }
                required
              />

              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={onClose} radius="xl" disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={() => void handleSave()} radius="xl" color="blue" loading={saving}>
                  Save
                </Button>
              </Group>
            </Stack>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
